import React, { useMemo } from 'react';
import { Dropdown, Button } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useSetAtom } from 'jotai';
import { settingsModalVisibleAtom, activeSettingsTabAtom } from '../NexaStore';
import { useNexaGateCheck } from '../utils/nexaGateCheck';
import useCanManageSysPrompt from '../SystemPrompt/hooks/useCanManageSysPrompt';

export const SettingsMenu: React.FC<{
  onSystemPromptClick: () => void;
  onDiagnosticTemplatesClick: () => void;
  onManageAnalysesClick: () => void;
  onManageSchedulesClick: () => void;
}> = ({
  onSystemPromptClick,
  onDiagnosticTemplatesClick,
  onManageAnalysesClick,
  onManageSchedulesClick,
}) => {
  const setSettingsModalVisible = useSetAtom(settingsModalVisibleAtom);
  const setActiveSettingsTab = useSetAtom(activeSettingsTabAtom);
  const { canEnable } = useNexaGateCheck();
  const canManageSysPrompt = useCanManageSysPrompt();

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'system-prompt') {
      onSystemPromptClick();
      return;
    }

    if (key === 'diagnostic-templates') {
      onDiagnosticTemplatesClick();
      return;
    }

    if (key === 'manage-analyses') {
      onManageAnalysesClick();
      return;
    }

    if (key === 'manage-schedules') {
      onManageSchedulesClick();
      return;
    }

    setActiveSettingsTab(key);
    setSettingsModalVisible(true);
  };

  const menuItems = useMemo(() => {
    const items: any[] = [];

    // System Prompt option (if permitted)
    if (canManageSysPrompt) {
      items.push({
        key: 'system-prompt',
        label: 'System Prompt',
      });
      items.push({
        key: 'diagnostic-templates',
        label: 'Diagnostic Templates',
      });
    }

    // Manage Analyses option (available to all users)
    items.push({
      key: 'manage-analyses',
      label: 'Manage Analyses',
    });

    // Manage Schedules option (available to all users)
    items.push({
      key: 'manage-schedules',
      label: 'Manage Schedules',
    });

    // Always show Options
    items.push({
      key: 'options',
      label: 'Options',
    });

    // Show Disable Nexa if user has canEnable permission
    if (canEnable) {
      items.push({
        key: 'disable-nexa',
        label: 'Disable Nexa',
      });
    }

    return items;
  }, [canEnable, canManageSysPrompt]);

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: handleMenuClick,
      }}
      trigger={['click']}
      placement="bottomLeft"
    >
      <Button icon={<SettingOutlined />} />
    </Dropdown>
  );
};
